import React, { useEffect, useState } from 'react';
import { BirdzNotif, BirdzTitle, useNotif } from '@applications-terrains/birdz-react-library';
import {
  Alert,
  Box,
  Button,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import SelectWithSearchModal from '../../../Tools/Selects/SelectWithSearchModal';
import axios, { AxiosError } from 'axios';
import { Form, Formik, FormikValues } from 'formik';
import { useGetCompanies } from '../../../../hooks/datarefs';
import {
  ModalWithLoaderAction,
  RequestStatus
} from '../../../Tools/ModalWithLoader/ModalWithLoader';
import { useTranslation } from 'react-i18next';

export default function ReasonForm({
  endpoint,
  setRequestStatus,
  action,
  setAction,
  id
}: {
  endpoint: string;
  setRequestStatus: (value: RequestStatus) => void;
  action: ModalWithLoaderAction;
  setAction: (value: ModalWithLoaderAction) => void;
  id: number | null;
}) {
  const { t } = useTranslation();
  const { notif, notifOptions } = useNotif();
  const [values, setValues] = useState<FormikValues>();
  const [isError, setIsError] = useState<boolean>();
  const initialValues = {
    Name: null,
    DefaultLabel: null,
    companies: [],
    translations: []
  };
  const { data: clients } = useGetCompanies();
  const [error, setError] = useState<Record<string, string>>();

  useEffect(() => {
    if (action === 'edit' && id !== null) {
      axios.get(endpoint + `${id}/`).then((response) => {
        setValues(response.data);
      });
    }
  }, [action, id]);

  const handleSubmit = async (values: FormikValues) => {
    const payload = {
      ...values,
      // On normalise les traductions au format attendu par le PUT
      translations: values.translations
        ?.filter(
          (tr: { lcid?: string; locale_id?: number; name: string }) => tr.name?.trim() !== ''
        )
        .map((tr: { lcid?: string; locale_id?: number; name: string }) => ({
          locale_id: tr.locale_id ?? LOCALE_MAP.find((loc) => loc.lcid === tr.lcid)?.locale_id,
          name: tr.name
        }))
    };

    if (!id) {
      await axios
        .post(endpoint, payload)
        .then(() => {
          setRequestStatus('success');
          setTimeout(() => {
            setRequestStatus(null);
            setAction(null);
          }, 1500);
        })
        .catch((error) => {
          const result = error.response.data as Record<string, string>;
          setError(result);
          {
            !result &&
              notif({
                content: t('anErrorOccurred'),
                type: 'error'
              });
          }
        });
    } else {
      await axios
        .put(endpoint + `${id}`, payload)
        .then(() => {
          setRequestStatus('success');
          setTimeout(() => {
            setRequestStatus(null);
            setAction(null);
          }, 1500);
        })
        .catch((error: AxiosError<Record<string, string>>) => {
          const result = error.response?.data;
          setError(result);
          {
            !result &&
              notif({
                content: t('anErrorOccurred'),
                type: 'error'
              });
          }
        });
    }
  };

  const LOCALE_MAP = [
    { locale_id: 1, lcid: 'en', label: t('referencesTables.english') },
    { locale_id: 2, lcid: 'fr', label: t('referencesTables.french') }
  ];

  return (
    <>
      <BirdzTitle>{t('referencesTables.reason')}</BirdzTitle>
      <Formik
        initialValues={values ? values : initialValues}
        onSubmit={handleSubmit}
        validateOnChange={true}
        validateOnBlur={true}
        enableReinitialize={true}
      >
        {({ values, setFieldValue }) => {
          const needRequiredFields = !values.Name || !values.DefaultLabel;
          setIsError(needRequiredFields);
          return (
            <Form>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <Box sx={{ position: 'relative' }}>
                    <TextField
                      size="small"
                      type={'text'}
                      name={'Name'}
                      value={values.Name || ''}
                      fullWidth
                      onChange={(e) => {
                        setFieldValue('Name', e.target.value);
                      }}
                      label={t('referencesTables.technicalName') + '*'}
                      InputLabelProps={{ shrink: true }}
                    />
                    {values.Name && (
                      <IconButton
                        size="small"
                        onClick={() => setFieldValue('Name', null)}
                        sx={{
                          position: 'absolute',
                          right: 8,
                          top: '50%',
                          transform: 'translateY(-50%)'
                        }}
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                  {error && 'Name' in error && (
                    <Alert sx={{ mt: 1 }} severity="error">
                      {error['Name']}
                    </Alert>
                  )}
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ position: 'relative' }}>
                    <TextField
                      size="small"
                      type={'text'}
                      name={'DefaultLabel'}
                      value={values.DefaultLabel || ''}
                      fullWidth
                      onChange={(e) => {
                        setFieldValue('DefaultLabel', e.target.value);
                      }}
                      label={t('referencesTables.defaultLabel') + '*'}
                      InputLabelProps={{ shrink: true }}
                    />
                    {values.DefaultLabel && (
                      <IconButton
                        size="small"
                        onClick={() => setFieldValue('DefaultLabel', null)}
                        sx={{
                          position: 'absolute',
                          right: 8,
                          top: '50%',
                          transform: 'translateY(-50%)'
                        }}
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <SelectWithSearchModal
                    multiple
                    label={t('referencesTables.clients')}
                    options={(clients ?? []).map((c) => ({ label: c.Name, value: c.Id }))}
                    value={values.companies ?? []}
                    onChange={(val) => setFieldValue('companies', val)}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('referencesTables.locale')}</TableCell>
                        <TableCell>{t('referencesTables.label')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {LOCALE_MAP.map(({ locale_id, lcid, label }) => {
                        // Pour l'affichage : on cherche par lcid (format retourné par l'API)
                        // ou par locale_id (format après modification)
                        const translation = values.translations?.find(
                          (tr: { lcid?: string; locale_id?: number; name: string }) =>
                            tr.lcid === lcid || tr.locale_id === locale_id
                        );
                        return (
                          <TableRow key={locale_id}>
                            <TableCell width={120}>{label}</TableCell>
                            <TableCell>
                              <TextField
                                size="small"
                                fullWidth
                                value={translation?.name || ''}
                                onChange={(e) => {
                                  const updatedTranslations = LOCALE_MAP.map((loc) => {
                                    const existing = values.translations?.find(
                                      (tr: { lcid?: string; locale_id?: number; name: string }) =>
                                        tr.lcid === loc.lcid || tr.locale_id === loc.locale_id
                                    );
                                    if (loc.locale_id === locale_id) {
                                      // On retourne le format attendu par le PUT
                                      return { locale_id: loc.locale_id, name: e.target.value };
                                    }
                                    // On convertit également les autres entrées au bon format
                                    return { locale_id: loc.locale_id, name: existing?.name || '' };
                                  });
                                  setFieldValue('translations', updatedTranslations);
                                }}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </Grid>
              </Grid>

              <Grid container justifyContent="center" sx={{ mt: 2 }}>
                <Grid item>
                  <Button
                    sx={{ ml: 2 }}
                    onClick={() => {
                      setRequestStatus(null);
                      setAction(null);
                    }}
                  >
                    {t('referencesTables.cancel')}
                  </Button>
                  <Button type="submit" variant="contained" disabled={isError}>
                    {action === 'edit' ? t('referencesTables.edit') : t('referencesTables.add')}
                  </Button>
                </Grid>
              </Grid>
            </Form>
          );
        }}
      </Formik>

      <BirdzNotif options={notifOptions} />
    </>
  );
}
